import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { verifyAdCompletion } from '../services/api';
import { useUser } from './UserContext';
import storage from '../platform/storage';

const AdContext = createContext();

export const useAd = () => useContext(AdContext);

export const AdProvider = ({ children }) => {
  const [showAd, setShowAd] = useState(false);
  const [showEarnTokenModal, setShowEarnTokenModal] = useState(false);
  const [adCompleted, setAdCompleted] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingPromise, setPendingPromise] = useState(null);
  // 0 until the stored time is loaded; App.js holds rendering until `hydrated` is true
  const [lastAdTime, setLastAdTime] = useState(0);
  const [hydrated, setHydrated] = useState(false);
  const adTimeoutRef = useRef(null);
  const { userData } = useUser();

  // Ad rate limiting - minimum 5 minutes between ads
  const AD_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
  const AD_TIMEOUT_MS = 5000; // 5 seconds timeout for ad loading

  // Load the stored last ad time on mount
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      const saved = await storage.getItem('lastAdTime');
      if (cancelled) return;
      // an ad completed while loading is newer than the stored time
      setLastAdTime(prev => prev || (saved ? parseInt(saved, 10) : 0));
      setHydrated(true);
    };

    hydrate();
    return () => { cancelled = true; };
  }, []);

  const canShowAd = () => {
    const now = Date.now();
    return (now - lastAdTime) >= AD_COOLDOWN_MS;
  };
  
  // Handle ad timeout (5 seconds)
  const handleAdTimeout = () => {
    console.log('Ad loading timeout - showing EarnTokenModal');
    handleAdError('Ad loading timeout');
  };
  
  // Setup timeout when ad is shown
  useEffect(() => {
    if (showAd) {
      console.log('Setting up 5-second ad timeout');
      adTimeoutRef.current = setTimeout(handleAdTimeout, AD_TIMEOUT_MS);
      
      return () => {
        if (adTimeoutRef.current) {
          clearTimeout(adTimeoutRef.current);
          adTimeoutRef.current = null;
        }
      };
    }
  }, [showAd]);
  
  // Function to check if an action requires an ad and handle it
  const checkAndHandleAdRequirement = async (actionFn) => {
    try {
      // Try to perform the action
      return await actionFn();
    } catch (error) {
      console.log('Checking if action requires ad:', error);
      
      // If the error indicates an ad is required (402 status code)
      if (error.response?.status === 402) {
        console.log('Ad required, checking rate limiting');
        
        // Check if we can show an ad (rate limiting)
        if (!canShowAd()) {
          const timeRemaining = AD_COOLDOWN_MS - (Date.now() - lastAdTime);
          const minutesRemaining = Math.ceil(timeRemaining / (60 * 1000));
          throw new Error(`Please wait ${minutesRemaining} more minutes before watching another ad`);
        }
        
        console.log('Ad approved, showing ad overlay');
        
        // Store the action to be executed after ad completion
        setPendingAction(() => actionFn);
        
        // Show the ad
        console.log('Setting showAd to true');
        setShowAd(true);
        console.log('showAd is now:', true);
        
        // Return a promise that will be resolved when the ad is completed
        return new Promise((resolve, reject) => {
          // Store the promise resolvers so they can be called later
          setPendingPromise({ resolve, reject });
        });
      }
      
      // If it's not an ad-related error, just re-throw it
      throw error;
    }
  };
  
  // Called when an ad is successfully completed
  const handleAdCompleted = async (adData) => {
    console.log('Ad completed:', adData);
    setAdCompleted(true);
    
    // Clear the timeout since ad completed successfully
    if (adTimeoutRef.current) {
      clearTimeout(adTimeoutRef.current);
      adTimeoutRef.current = null;
    }
    
    // Record the ad completion time for rate limiting
    const now = Date.now();
    setLastAdTime(now);
    storage.setItem('lastAdTime', now.toString());
    
    // Hide the ad overlay
    setShowAd(false);
    
    try {
      // First, verify the ad completion with the backend
      console.log('Verifying ad completion with backend...');
      const verificationPayload = {
        ad_providers: [
          {
            code: "google",
            name: "Google AdSense",
            priority: "1"
          }
        ],
        provider_used: "google",
        provider_data: {
          ad_id: adData.ad_id || 'unknown',
          instance_id: adData.instance_id || new Date().getTime().toString()
        },
        country: userData?.countryCode || 'unknown',
        language: userData?.languageCode || 'en',
        platform: 'web'
      };
      
      const verificationResult = await verifyAdCompletion(verificationPayload);
      console.log('Ad verification successful:', verificationResult);
      
      // If there's a pending action, execute it
      if (pendingAction) {
        console.log('Executing pending action after ad completion');
        const result = await pendingAction();
        console.log('Pending action completed successfully:', result);
        
        // Resolve the pending promise with the result
        if (pendingPromise) {
          pendingPromise.resolve(result);
        }
        
        // Clean up
        setPendingAction(null);
        setPendingPromise(null);
        return result;
      } else {
        // No pending action, just resolve the promise
        if (pendingPromise) {
          pendingPromise.resolve(verificationResult);
        }
        setPendingPromise(null);
      }
    } catch (error) {
      console.error('Error verifying ad or executing action:', error);
      
      // Reject the pending promise with the error
      if (pendingPromise) {
        pendingPromise.reject(error);
      }
      
      // Clean up
      setPendingAction(null);
      setPendingPromise(null);
      throw error;
    }
  };

  
  // Called when there's an error with the ad
  const handleAdError = (error) => {
    console.error('Ad error:', error);
    
    // Clear the timeout
    if (adTimeoutRef.current) {
      clearTimeout(adTimeoutRef.current);
      adTimeoutRef.current = null;
    }
    
    setShowAd(false);
    
    // Show the EarnTokenModal as fallback
    setShowEarnTokenModal(true);
    
    // Reject the pending promise with the error
    if (pendingPromise) {
      pendingPromise.reject(new Error(`Ad failed: ${error}`));
    }
    
    // Clean up
    setPendingAction(null);
    setPendingPromise(null);
  };
  
  // Show EarnTokenModal (can be called from wallet icon or other places)
  const showEarnTokenModalFunc = () => {
    setShowEarnTokenModal(true);
  };
  
  // Hide EarnTokenModal
  const hideEarnTokenModal = () => {
    setShowEarnTokenModal(false);
  };
  
  // Called when an ad is loaded and ready to play
  const handleAdLoaded = () => {
    console.log('Ad loaded and ready to play');
    // Clear the timeout since ad loaded successfully
    if (adTimeoutRef.current) {
      clearTimeout(adTimeoutRef.current);
      adTimeoutRef.current = null;
    }
  };
  
  return (
    <AdContext.Provider
      value={{
        hydrated,
        showAd,
        showEarnTokenModal,
        adCompleted,
        canShowAd,
        checkAndHandleAdRequirement,
        handleAdCompleted,
        handleAdError,
        handleAdLoaded,
        showEarnTokenModalFunc,
        hideEarnTokenModal,
      }}
    >
      {children}
    </AdContext.Provider>
  );
};
